/**
 * ChatMemory — Dev mode → Knowledge → Chat memory (mockups/kiaan-knowledge.html, screen 1b). Admin only.
 * Pick an agent → its chats → open one → pick one line, the whole chat, or everything the agent
 * remembers. The server's preview (how many chats / messages / sessions) shows before Forget.
 * Forgetting redacts the text and resets the agent's session; the original stays in the admin audit for 30 days.
 */
import './knowledge.css';
import { useEffect, useState } from 'react';
import {
  forgetMemory, getMemoryMessages, knowledgeErrorText, knowledgeLoadErrorText, listMemory, previewForget,
  type AgentMemory, type ForgetBody, type ForgetPreview, type ForgetScope, type MemoryMessage,
} from './knowledgeApi';
import { whenText } from './whenText';

export function ChatMemory({ agentId: initialAgent = null }: { agentId?: string | null }) {
  const [list, setList] = useState<AgentMemory[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [agentId, setAgentId] = useState<string | null>(initialAgent);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<MemoryMessage[] | null>(null);
  const [messageId, setMessageId] = useState<string | null>(null);
  const [scope, setScope] = useState<ForgetScope>('chat');
  const [preview, setPreview] = useState<ForgetPreview | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => { if (initialAgent) setAgentId(initialAgent); }, [initialAgent]);

  useEffect(() => {
    let alive = true;
    listMemory()
      .then((l) => {
        if (!alive) return;
        setList(l);
        setError(null);
        setAgentId((a) => a ?? l[0]?.agentId ?? null);
      })
      .catch((e) => { if (alive) setError(knowledgeLoadErrorText(e)); });
    return () => { alive = false; };
  }, [tick]);

  useEffect(() => {
    setMessages(null);
    setMessageId(null);
    if (!agentId || !threadId) return;
    let alive = true;
    getMemoryMessages(agentId, threadId)
      .then((m) => { if (alive) setMessages(m); })
      .catch((e) => { if (alive) setError(knowledgeErrorText(e)); });
    return () => { alive = false; };
  }, [agentId, threadId, tick]);

  const body: ForgetBody | null = !agentId ? null
    : scope === 'agent' ? { scope, agentId }
    : scope === 'chat' && threadId ? { scope, agentId, threadId }
    : scope === 'message' && threadId && messageId ? { scope, agentId, threadId, messageId }
    : null;
  const bodyKey = body ? JSON.stringify(body) : '';

  useEffect(() => {
    setPreview(null);
    setConfirming(false);
    if (!body) return;
    let alive = true;
    previewForget(body)
      .then((p) => { if (alive) setPreview(p); })
      .catch((e) => { if (alive) setError(knowledgeErrorText(e)); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyKey]);

  const forget = async () => {
    if (!body) return;
    setBusy(true);
    setError(null);
    try {
      const r = await forgetMemory(body);
      setDone(`Forgotten: ${r.redacted} ${r.redacted === 1 ? 'message' : 'messages'} removed, ${r.chatsArchived} ${r.chatsArchived === 1 ? 'chat' : 'chats'} archived, ${r.sessionsReset} ${r.sessionsReset === 1 ? 'session' : 'sessions'} reset.`);
      setConfirming(false);
      if (scope !== 'message') { setThreadId(null); setScope('chat'); }
      setTick((t) => t + 1);
    } catch (e) {
      setError(knowledgeErrorText(e));
    } finally {
      setBusy(false);
    }
  };

  if (!list) return error ? <div className="kw-banner" role="status">{error}</div> : <div className="kw-small" style={{ padding: 12 }}>Loading…</div>;

  const agent = list.find((a) => a.agentId === agentId) ?? null;
  const chat = agent?.chats.find((c) => c.threadId === threadId) ?? null;
  const msg = messages?.find((m) => m.id === messageId) ?? null;

  return (
    <div className="kn-mem" data-testid="chat-memory">
      <aside className="kn-pane kn-l" aria-label="Agents">
        <div className="kn-h4">Chat memories</div>
        {list.length === 0 && <div className="kw-small">No agent chats yet.</div>}
        {list.map((a) => (
          <button key={a.agentId} type="button" className={`kn-ti${a.agentId === agentId ? ' kn-sel' : ''}`} onClick={() => { setAgentId(a.agentId); setThreadId(null); setScope('chat'); setDone(null); }}>
            <span className="kn-sq" style={{ background: '#0F766E', borderRadius: '50%' }} /><b>{a.name}</b><span className="kn-cnt">{a.chatCount} chats</span>
          </button>
        ))}
      </aside>

      <section className="kn-pane" aria-label="Chats">
        {agent && (
          <>
            <div className="kn-h4">{agent.name} · chat memories ({agent.chatCount} {agent.chatCount === 1 ? 'chat' : 'chats'})</div>
            <table className="kn-table">
              <thead><tr><th>Chat</th><th>Where</th><th>Last used</th><th>Messages</th><th /></tr></thead>
              <tbody>
                {agent.chats.map((c) => (
                  <tr key={c.threadId} className={c.threadId === threadId ? 'kn-trsel' : undefined}>
                    <td>{c.threadId === threadId ? <b>{c.title}</b> : c.title}{c.archived && <span className="kw-pill kw-no kw-ml">archived</span>}</td>
                    <td>{c.channel ? 'Channel' : 'Direct chat'}</td>
                    <td>{whenText(c.lastAt)}</td>
                    <td>{c.messages}</td>
                    <td><button type="button" className="kw-btn kw-light" style={{ margin: 0 }} aria-label={`Open ${c.title}`} onClick={() => { setThreadId(c.threadId); setScope('chat'); setDone(null); }}>Open</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {chat && (
              <div className="kn-msgs" aria-label="Messages">
                <div className="kn-h4">Messages — click one to forget just that line</div>
                {messages === null && <div className="kw-small">Loading…</div>}
                {messages?.map((m) => (
                  <button key={m.id} type="button" disabled={m.removed} className={`kn-msg${m.id === messageId ? ' kn-sel' : ''}${m.removed ? ' kn-removed' : ''}`}
                    onClick={() => { setMessageId(m.id); setScope('message'); setDone(null); }}>
                    <span className="kw-small">{m.name} · {whenText(m.at)}</span>
                    <span>{m.removed ? '[removed]' : m.text}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <aside className="kn-pane kn-r" aria-label="Forget">
        {agent ? (
          <>
            <div className="kn-h4">Selected · chat memory</div>
            <b className="kn-title">{chat ? `${chat.title} · ${agent.name}` : agent.name}</b>
            <div className="kn-kv">
              <span>Messages</span><span>{chat ? chat.messages : agent.messageCount}</span>
              <span>Stored as</span><span>Kiaan Work chat + {agent.name}'s session for it</span>
            </div>
            <div className="kn-warn" role="radiogroup" aria-label="Choose what to forget">
              <b>Choose what to forget</b>
              <label><input type="radio" name="kn-scope" checked={scope === 'message'} disabled={!msg} onChange={() => setScope('message')} /> Just this line{msg ? `: “${msg.text.length > 60 ? `${msg.text.slice(0, 59)}…` : msg.text}”` : ' (open a chat, click a message)'}</label>
              <label><input type="radio" name="kn-scope" checked={scope === 'chat'} disabled={!chat} onChange={() => setScope('chat')} /> This whole chat{chat ? ` (${chat.messages} messages)` : ' (open a chat)'}</label>
              <label><input type="radio" name="kn-scope" checked={scope === 'agent'} onChange={() => setScope('agent')} /> Everything {agent.name} remembers ({agent.chatCount} {agent.chatCount === 1 ? 'chat' : 'chats'})</label>
              <span>The agent's next answer will not use what you remove. The chat text stays in the audit log for 30 days, readable by admins only.</span>
            </div>
            {preview && (
              <div className="kn-card" data-testid="forget-preview">
                Will forget {preview.messages} {preview.messages === 1 ? 'message' : 'messages'} in {preview.chats} {preview.chats === 1 ? 'chat' : 'chats'} of {preview.agentName}
                {preview.willResetSessions > 0 ? ` and reset ${preview.willResetSessions} ${preview.willResetSessions === 1 ? 'session' : 'sessions'}` : ''}.
              </div>
            )}
            {error && <div className="kw-banner kw-danger" role="alert">{error}</div>}
            {done && <div className="kw-banner kw-ok" role="status">{done}</div>}
            {!confirming ? (
              <div className="kn-row">
                <button type="button" className="kw-btn kn-red" disabled={!preview || busy} onClick={() => setConfirming(true)}>Forget selected</button>
                <button type="button" className="kw-btn kw-light" onClick={() => { setThreadId(null); setScope('chat'); }}>Cancel</button>
              </div>
            ) : (
              <div className="kn-confirm" role="dialog" aria-label="Confirm forget">
                <span>Sure? {scope === 'agent' ? `All of ${agent.name}'s chats are archived and cleared.` : 'This cannot be undone here.'}</span>
                <div className="kn-row">
                  <button type="button" className="kw-btn kn-red" disabled={busy} onClick={() => void forget()}>{busy ? 'Forgetting…' : 'Yes, forget'}</button>
                  <button type="button" className="kw-btn kw-light" disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="kw-small">Pick an agent on the left.</div>
        )}
      </aside>
    </div>
  );
}
